/**
 * Message Thunks
 *
 * Async thunks for chat message operations
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  type UserMessage,
  type AssistantMessage,
  type ApiCallPayload,
  type ChatStreamRequest,
  ApiCallIdentifier,
} from '@contracts';
import {
  sendChatSSE,
  fetchChatSummary,
  fetchTemplateDetails,
  sendChatStream,
} from '../api/chatApi';
import {
  addUserMessage,
  updateStreamingMessage,
  completeStreaming,
  addThinkingMessage,
  clearThinkingMessages,
  setStreamingError,
  setMessages,
  setLoading,
  prependMessages,
  setLoadingMore,
} from '../slices/messagesSlice';
import { updateTemplateFromStream, setActiveTemplate } from '../slices/templateSlice';
import { registerStreamAbortFn, clearStreamAbortFn } from '../../utils/streamAbortManager';
import { pollAsyncTaskThunk } from './pollThunks';
import { fetchFlightInterstitialThunk } from '@/features/flights/redux';
import { FEATURE_FLAGS } from '@/features/flights/constants';
import { TRACKING_DATA, fireTrackingEvents, logPageLoad, TRACKING_IDS } from '@shared/analytics';
import { getOrCreateSessionId } from '@/shared/utils';
import { getSessionIdFromStorage, getSessionIdFromUrl, getJwtToken } from '@/shared/utils/session';
import { getUUID } from '@/shared/utils/appCookies';
import { logger } from '@/shared/utils/logger';
import type { AppDispatch, RootState } from '@/store/store';
import { createSessionThunk } from '@/features/auth/redux';
import { resolveRenderableTemplate } from '@/features/chat/utils';
import { MOCK_FINAL_RESPONSE_WITH_ASYNC_TASK } from '@/api/contracts/templates/flights/api-response/chat-stream-async-poll';

const DEFAULT_CHAT_HISTORY_LIMIT = 10;

/**
 * Injects mock asyncTask and action-button content into a real stream response.
 * Used for local testing when USE_MOCK_ASYNC_POLL is enabled.
 */
function injectMockAsyncTask(original: AssistantMessage): AssistantMessage {
  const mockResponse = MOCK_FINAL_RESPONSE_WITH_ASYNC_TASK.response;
  return {
    ...original,
    content: [
      ...original.content,
      ...mockResponse.content.filter((c) => c.type === 'action-button'),
    ],
    asyncTask: mockResponse.asyncTask,
  };
}

// ============================================
// Thunks
// ============================================

/**
 * Convert FINAL_RESPONSE event to AssistantMessage format
 */
// function convertFinalResponseToAssistantMessage(
//   event: ChatStreamFinalResponseEvent,
// ): AssistantMessage {
//   const { response } = event;
//   // const message: AssistantMessage = {
//   //   id: response.id,
//   //   role: 'assistant',
//   //   type: response.type,
//   //   content: response.content,
//   //   templateData: response.templateData,
//   //   timestamp: new Date(event.timestamp).toISOString(),
//   // };

//   // // Only add activeTemplate if it's not null
//   // if (response.activeTemplate !== null) {
//   //   message.activeTemplate = response.activeTemplate;
//   // }

//   // // Only add meta if it exists
//   // if (response.meta !== undefined) {
//   //   message.meta = response.meta;
//   // }

//   return response;
// }

/**
 * Send a message and stream the response using the new chat stream API.
 * Handles THINKING, FINAL_RESPONSE, and STREAM_END events.
 *
 * @param content - The user's message content
 * @param lob - Optional line of business (e.g., 'flights', 'hotels')
 */
export const sendMessageStreamThunk = createAsyncThunk<
  void,
  { content: string },
  { dispatch: AppDispatch; rejectValue: string }
>('chat/sendMessageStream', async ({ content }, { dispatch, rejectWithValue }) => {
  try {
    // Ensure a session with JWT token exists before making any API call
    const hadSessionBefore = !!getSessionIdFromStorage();
    if (!hadSessionBefore || !getJwtToken()) {
      const result = await dispatch(createSessionThunk()).unwrap();
      if (!result) {
        return rejectWithValue('Failed to create session');
      }
    }

    const sessionId = getOrCreateSessionId();

    if (!hadSessionBefore) {
      logPageLoad({ prop54: TRACKING_IDS.CONVERSATION_STARTED, eVar50: sessionId });
    }

    // Add user message and placeholder
    dispatch(addUserMessage(content));

    // Build chat stream request
    const request: ChatStreamRequest = {
      crId: sessionId,
      query: content,
      sessionId,
      userId: getUUID(),
      // sending a random ID for the lob as asked by CBackend
      lob: 'flights',
    };

    // Send chat stream request - returns immediately with abort handle
    const streamHandle = sendChatStream(request, {
      onThinking: (event) => {
        logger.debug('THINKING_EVENT', event.thinkingMessage);
        dispatch(addThinkingMessage(event.thinkingMessage));
      },
      onFinalResponse: (event) => {
        logger.debug('FINAL_RESPONSE_EVENT', event);
        dispatch(clearThinkingMessages());

        let assistantMessage =
          FEATURE_FLAGS.ASYNC_TASK_POLLING && FEATURE_FLAGS.USE_MOCK_ASYNC_POLL
            ? injectMockAsyncTask(event.response)
            : event.response;

        // TODO: Replace with backend-driven trackingInfo from API response
        if (assistantMessage.activeTemplate && !assistantMessage.trackingInfo) {
          const trackingData = TRACKING_DATA[assistantMessage.activeTemplate];
          if (trackingData) {
            assistantMessage = { ...assistantMessage, trackingInfo: trackingData };
          }
        }

        if (assistantMessage.trackingInfo) {
          fireTrackingEvents(assistantMessage.trackingInfo);
        }

        dispatch(updateStreamingMessage(assistantMessage));

        if (FEATURE_FLAGS.ASYNC_TASK_POLLING && assistantMessage.asyncTask) {
          const {
            pollEndpoint,
            pollMethod,
            params: asyncParams,
            pollIntervalMs,
            maxAttempts,
            maxNetworkRetries,
            networkRetryDelayMs,
          } = assistantMessage.asyncTask;
          void dispatch(
            pollAsyncTaskThunk({
              pollEndpoint,
              pollMethod,
              params: asyncParams ?? {},
              messageId: assistantMessage.id,
              ...(pollIntervalMs !== undefined && { pollIntervalMs }),
              ...(maxAttempts !== undefined && { maxAttempts }),
              ...(maxNetworkRetries !== undefined && { maxNetworkRetries }),
              ...(networkRetryDelayMs !== undefined && { networkRetryDelayMs }),
            }),
          );
        } else {
          dispatch(updateTemplateFromStream(assistantMessage));
        }
      },
      onStreamEnd: () => {
        logger.debug('STREAM_END_EVENT');
        clearStreamAbortFn();
        dispatch(completeStreaming());
      },
      onError: (error) => {
        logger.error('Chat stream error:', error);
        clearStreamAbortFn();
        dispatch(setStreamingError(error.message));
      },
    });

    // Register the abort function IMMEDIATELY so it can be called from ChatBox
    // This works even while the request is still pending (before 200 OK)
    registerStreamAbortFn(streamHandle.abort);

    // Wait for stream to complete (or be aborted)
    await streamHandle.promise;

    return;
  } catch (error) {
    logger.error('Error sending message stream:', error);
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to send message');
  }
});

/**
 * Send a message and stream the response (Legacy - uses old SSE endpoint)
 * @deprecated Use sendMessageStreamThunk instead for new implementations
 */
export const _sendMessageThunk = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; rejectValue: string }
>('chat/_sendMessage', async (content, { dispatch, rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();

    // Add user message and placeholder
    dispatch(addUserMessage(content));

    // Create contract user message (matches @contracts UserMessage structure)
    const userMessage: UserMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    // Send SSE request using old API
    await sendChatSSE(
      userMessage,
      sessionId,
      (streamedMessage) => {
        logger.debug('SSE_CHUNK', streamedMessage);
        dispatch(updateStreamingMessage(streamedMessage));
        // Update template from streamed message (for flights, itinerary, hotels, etc.)
        dispatch(updateTemplateFromStream(streamedMessage));
      },
      () => {
        logger.debug('SSE stream completed');
        dispatch(completeStreaming());
      },
      (error) => {
        logger.error('SSE stream error:', error);
        dispatch(setStreamingError(error.message));
      },
    );

    return;
  } catch (error) {
    logger.error('Error sending message:', error);
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to send message');
  }
});

/**
 * Send a message and stream the response.
 * Uses the new chat stream API with THINKING, FINAL_RESPONSE, STREAM_END events.
 */
export const sendMessageThunk = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch; rejectValue: string }
>('chat/sendMessage', async (content, { dispatch, rejectWithValue }) => {
  // Delegate to the new stream thunk with default lob
  try {
    await dispatch(sendMessageStreamThunk({ content })).unwrap();
    return;
  } catch (error) {
    logger.error('Error sending message:', error);
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to send message');
  }
});

/**
 * Load chat history for a session (initial load)
 *
 * Fires summary + template-details requests in PARALLEL for maximum speed:
 * - Summary resolves  → left panel renders immediately (messages)
 * - Template resolves → right panel renders (activeTemplate + templateData)
 *
 * Error handling is DECOUPLED: each request is handled independently so a
 * summary failure doesn't abandon the template, and vice versa.
 */
interface LoadChatHistoryParams {
  skipSetTemplate?: boolean;
}

export const loadChatHistoryThunk = createAsyncThunk<
  void,
  LoadChatHistoryParams | void,
  { dispatch: AppDispatch; rejectValue: string }
>('chat/loadChatHistory', async (params, { dispatch, rejectWithValue }) => {
  const skipSetTemplate = params?.skipSetTemplate ?? false;

  const sessionId = getSessionIdFromUrl();
  if (!sessionId) {
    return rejectWithValue('No session ID found');
  }

  const userId = getUUID();
  dispatch(setLoading(true));

  // Fire both requests in parallel.
  // Attach .catch to templatePromise immediately so it can't become an
  // unhandled rejection if the summary await throws first.
  const summaryPromise = fetchChatSummary({
    userId,
    sessionId,
    limit: DEFAULT_CHAT_HISTORY_LIMIT,
  });

  const templatePromise = skipSetTemplate
    ? null
    : fetchTemplateDetails({ userId, sessionId }).catch((error: unknown) => {
        logger.error('Error loading template details:', error);
        return null;
      });

  // ── Phase 1: Messages (left panel) ──────────────────────────────
  try {
    const summaryResponse = await summaryPromise;

    dispatch(
      setMessages({
        messages: summaryResponse.data.messages,
        hasMore: summaryResponse.data.hasMore,
        nextCursor: summaryResponse.data.nextCursor,
      }),
    );

    logger.debug('Chat summary loaded', {
      messagesCount: summaryResponse.data.messages.length,
      hasMore: summaryResponse.data.hasMore,
    });

    // ── Phase 2: Template details (right panel) ──────────────────
    // Template errors are already caught by the .catch() above —
    // templateResponse will be null on failure.
    if (templatePromise) {
      const templateResponse = await templatePromise;

      if (templateResponse) {
        const { activeTemplate, templateData } = resolveRenderableTemplate(
          templateResponse.data.activeTemplate,
          templateResponse.data.templateData,
          summaryResponse.data.messages,
        );

        dispatch(setActiveTemplate({ template: activeTemplate, data: templateData }));
        logger.debug('Template details loaded', {
          activeTemplate: templateResponse.data.activeTemplate,
        });
      }
    }

    dispatch(setLoading(false));
    return;
  } catch (error) {
    logger.error('Error loading chat history:', error);
    dispatch(setLoading(false));
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to load chat history');
  }
});

/**
 * Load more chat history (pagination - triggered on scroll)
 * Prepends older messages to the existing messages list.
 * Uses the summary endpoint since pagination only needs messages.
 */
export const loadMoreChatHistoryThunk = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>('chat/loadMoreChatHistory', async (_, { dispatch, getState, rejectWithValue }) => {
  try {
    const state = getState();
    const { hasMore, nextCursor, isLoadingMore } = state.chat.messages;

    if (isLoadingMore || !hasMore || !nextCursor) {
      return;
    }

    const sessionId = getSessionIdFromUrl();

    if (!sessionId) {
      return rejectWithValue('No session ID found');
    }

    dispatch(setLoadingMore(true));

    const response = await fetchChatSummary({
      userId: getUUID(),
      sessionId,
      cursor: nextCursor,
      limit: DEFAULT_CHAT_HISTORY_LIMIT,
    });

    dispatch(
      prependMessages({
        messages: response.data.messages,
        hasMore: response.data.hasMore,
        nextCursor: response.data.nextCursor,
      }),
    );

    logger.debug('More chat history loaded', {
      messagesCount: response.data.messages.length,
      hasMore: response.data.hasMore,
      nextCursor: response.data.nextCursor,
    });

    return;
  } catch (error) {
    logger.error('Error loading more chat history:', error);
    dispatch(setLoadingMore(false));
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to load more chat history');
  }
});

/**
 * Unified API action thunk
 * Routes to the appropriate API based on the API call identifier
 */
export const callApiActionThunk = createAsyncThunk<
  void,
  ApiCallPayload,
  { dispatch: AppDispatch; rejectValue: string }
>('chat/callApiAction', async (payload, { dispatch, rejectWithValue }) => {
  try {
    switch (payload.apiCallIdentifier) {
      case ApiCallIdentifier.GET_FLIGHT_LISTING: {
        // Call sendMessageThunk for flight listing it will return activeTemplate and templateData
        // We will use the activeTemplate and templateData to update the template in the store
        // probably we will add new thunk for flight listing and use it here instead of sendMessageThunk
        const listingPayload = payload;
        await dispatch(sendMessageThunk(listingPayload.query));
        // dispatch(setActiveTemplate({
        //   template: listingPayload.activeTemplate,
        //   data: listingPayload.templateData,
        // }));
        return;
      }

      case ApiCallIdentifier.GET_FLIGHT_REVIEW: {
        // Call flights interstitial API
        await dispatch(fetchFlightInterstitialThunk(payload));
        return;
      }

      default: {
        logger.error('[callApiActionThunk] Unknown API call identifier:', payload);
        return rejectWithValue(`Unknown API call identifier: ${String(payload)}`);
      }
    }
  } catch (error) {
    logger.error('[callApiActionThunk] Error calling API:', error);
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to call API');
  }
});
